import { apiRequest } from "./apiService";

const encId = (id) => encodeURIComponent(String(id == null ? "" : id));

const normalizeId = (value) => String(value == null ? "" : value).trim();

const normalizeHandle = (value) =>
  String(value == null ? "" : value).trim().replace(/^@/, "");

const isObject = (value) =>
  Boolean(value && typeof value === "object" && !Array.isArray(value));

const requireObjectPayload = (payload, label) => {
  if (!isObject(payload)) {
    throw new Error(`Valid payload is required for ${label}.`);
  }
};

const requireRequestId = (requestId) => {
  if (requestId == null || String(requestId).trim() === "") {
    throw new Error("requestId is required.");
  }
};

const requireBusinessId = (businessId) => {
  if (businessId == null || String(businessId).trim() === "") {
    throw new Error("businessId is required.");
  }
};

const requireCampaignId = (campaignId) => {
  if (campaignId == null || String(campaignId).trim() === "") {
    throw new Error("campaignId is required.");
  }
};

const requireStringField = (payload, fieldName) => {
  if (payload?.[fieldName] == null || String(payload[fieldName]).trim() === "") {
    throw new Error(`${fieldName} is required.`);
  }
};

const hasCreatorIdentifier = (payload) =>
  Boolean(
    normalizeId(payload?.creatorId) ||
      normalizeHandle(payload?.creatorHandle) ||
      normalizeId(payload?.creatorProfileUrl)
  );

const cleanStringArray = (value) => {
  if (!Array.isArray(value)) return [];

  return value
    .map((item) => String(item == null ? "" : item).trim())
    .filter(Boolean);
};

const buildQuery = (params = {}) => {
  const search = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value == null || value === "") return;

    if (key === "lastEvaluatedKey" && isObject(value)) {
      search.set(key, JSON.stringify(value));
      return;
    }

    search.set(key, String(value));
  });

  const qs = search.toString();
  return qs ? `?${qs}` : "";
};

const normalizeNumber = (value, fallback = undefined) => {
  if (value == null || value === "") return fallback;

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    throw new Error("Numeric value is invalid.");
  }

  return numeric;
};

const normalizeCurrency = (value) => {
  const currency = normalizeId(value || "GBP").toUpperCase();

  if (!/^[A-Z]{3}$/.test(currency)) {
    throw new Error("currency must be a 3-letter ISO code.");
  }

  return currency;
};

const normalizeDiscountType = (value) => {
  const discountType = normalizeId(value || "PERCENT").toUpperCase();

  if (!["PERCENT", "AMOUNT"].includes(discountType)) {
    throw new Error("discountType must be PERCENT or AMOUNT.");
  }

  return discountType;
};

const normalizeCreatorRequestCreatePayload = (payload) => {
  requireObjectPayload(payload, "creator request create");

  requireBusinessId(payload.businessId);

  if (!hasCreatorIdentifier(payload)) {
    throw new Error(
      "At least one creator identifier is required: creatorId, creatorHandle, or creatorProfileUrl."
    );
  }

  return {
    ...payload,
    businessId: normalizeId(payload.businessId),

    ...(payload.creatorId != null && payload.creatorId !== ""
      ? { creatorId: normalizeId(payload.creatorId) }
      : {}),

    ...(payload.creatorHandle != null && payload.creatorHandle !== ""
      ? { creatorHandle: normalizeHandle(payload.creatorHandle) }
      : {}),

    ...(payload.creatorProfileUrl != null && payload.creatorProfileUrl !== ""
      ? { creatorProfileUrl: normalizeId(payload.creatorProfileUrl) }
      : {}),

    platform: normalizeId(payload.platform || "instagram"),

    ...(payload.source ? { source: normalizeId(payload.source) } : {}),
    ...(payload.priority ? { priority: normalizeId(payload.priority) } : {}),

    ...(payload.inboundMessage != null
      ? { inboundMessage: String(payload.inboundMessage).trim() }
      : {}),

    ...(payload.requestedFee != null && payload.requestedFee !== ""
      ? { requestedFee: Number(payload.requestedFee) }
      : {}),

    ...(payload.requestedGift != null
      ? { requestedGift: String(payload.requestedGift).trim() }
      : {}),

    proposedDeliverables: cleanStringArray(payload.proposedDeliverables),

    ...(payload.staffNotes != null
      ? { staffNotes: String(payload.staffNotes).trim() }
      : {}),
  };
};

const normalizeCreatorRequestPatchPayload = (payload) => {
  requireObjectPayload(payload, "creator request patch");

  const body = {
    ...(payload.creatorId != null
      ? { creatorId: normalizeId(payload.creatorId) }
      : {}),

    ...(payload.creatorHandle != null
      ? { creatorHandle: normalizeHandle(payload.creatorHandle) }
      : {}),

    ...(payload.creatorProfileUrl != null
      ? { creatorProfileUrl: normalizeId(payload.creatorProfileUrl) }
      : {}),

    ...(payload.platform != null
      ? { platform: normalizeId(payload.platform) }
      : {}),

    ...(payload.source != null ? { source: normalizeId(payload.source) } : {}),

    ...(payload.priority != null
      ? { priority: normalizeId(payload.priority) }
      : {}),

    ...(payload.status != null ? { status: normalizeId(payload.status) } : {}),

    ...(payload.inboundMessage != null
      ? { inboundMessage: String(payload.inboundMessage).trim() }
      : {}),

    ...(payload.requestedFee != null && payload.requestedFee !== ""
      ? { requestedFee: Number(payload.requestedFee) }
      : {}),

    ...(payload.requestedGift != null
      ? { requestedGift: String(payload.requestedGift).trim() }
      : {}),

    ...(payload.proposedDeliverables != null
      ? { proposedDeliverables: cleanStringArray(payload.proposedDeliverables) }
      : {}),

    ...(payload.staffNotes != null
      ? { staffNotes: String(payload.staffNotes).trim() }
      : {}),
  };

  const hasAtLeastOneField = Object.values(body).some((value) => {
    if (Array.isArray(value)) return value.length > 0;
    return value !== undefined && value !== null && value !== "";
  });

  if (!hasAtLeastOneField) {
    throw new Error("At least one field is required to patch creator request.");
  }

  return body;
};

const normalizeDecisionRunPayload = (payload) => {
  requireObjectPayload(payload, "creator request decision");

  if (!payload.businessType) {
    throw new Error("businessType is required.");
  }

  if (!isObject(payload.businessLocation)) {
    throw new Error("businessLocation is required.");
  }

  if (!payload.campaignGoal) {
    throw new Error("campaignGoal is required.");
  }

  return {
    businessType: normalizeId(payload.businessType),
    businessLocation: payload.businessLocation,
    campaignGoal: String(payload.campaignGoal).trim(),
  };
};

const normalizeBusinessDecisionPayload = (payload) => {
  requireObjectPayload(payload, "creator request business decision");

  if (!payload.businessDecision) {
    throw new Error("businessDecision is required.");
  }

  return {
    businessDecision: normalizeId(payload.businessDecision),
    ...(payload.decisionNote != null
      ? { decisionNote: String(payload.decisionNote).trim() }
      : {}),
  };
};

const normalizeCampaignLinkPayload = (payload) => {
  requireObjectPayload(payload, "creator request campaign link");

  requireCampaignId(payload.campaignId);

  return {
    campaignId: normalizeId(payload.campaignId),
    ...(payload.offerId != null && payload.offerId !== ""
      ? { offerId: normalizeId(payload.offerId) }
      : {}),
    ...(payload.inviteId != null && payload.inviteId !== ""
      ? { inviteId: normalizeId(payload.inviteId) }
      : {}),
  };
};

const normalizeConvertToCampaignPayload = (payload) => {
  requireObjectPayload(payload, "creator request campaign conversion");

  requireStringField(payload, "campaignName");
  requireStringField(payload, "campaignGoal");
  requireStringField(payload, "offerTitle");
  requireStringField(payload, "landingUrl");

  const discountType = normalizeDiscountType(payload.discountType || "PERCENT");
  const discountValue = normalizeNumber(payload.discountValue, 10);

  if (discountValue == null || discountValue <= 0) {
    throw new Error("discountValue must be greater than 0.");
  }

  if (discountType === "PERCENT" && discountValue > 100) {
    throw new Error("discountValue cannot exceed 100 for PERCENT.");
  }

  return {
    campaignName: String(payload.campaignName).trim(),
    campaignGoal: String(payload.campaignGoal).trim(),
    offerTitle: String(payload.offerTitle).trim(),
    discountType,
    discountValue,
    currency: normalizeCurrency(payload.currency || "GBP"),
    landingUrl: String(payload.landingUrl).trim(),

    ...(payload.inviteMessage != null && payload.inviteMessage !== ""
      ? { inviteMessage: String(payload.inviteMessage).trim() }
      : {}),

    ...(payload.code != null && payload.code !== ""
      ? { code: normalizeId(payload.code).toUpperCase() }
      : {}),
  };
};

/**
 * POST /creator-requests
 * Body: CreatorRequestCreateRequest
 */
export const createCreatorRequest = (payload) => {
  const body = normalizeCreatorRequestCreatePayload(payload);

  return apiRequest("/creator-requests", "POST", body);
};

/**
 * GET /creator-requests/{requestId}
 */
export const getCreatorRequest = (requestId) => {
  requireRequestId(requestId);

  return apiRequest(`/creator-requests/${encId(requestId)}`, "GET");
};

/**
 * GET /creator-requests?businessId=...&limit=...&lastEvaluatedKey=...
 */
export const listCreatorRequests = ({
  businessId,
  limit = 50,
  lastEvaluatedKey = null,
} = {}) => {
  requireBusinessId(businessId);

  const query = buildQuery({
    businessId: normalizeId(businessId),
    limit,
    lastEvaluatedKey,
  });

  return apiRequest(`/creator-requests${query}`, "GET");
};

/**
 * PATCH /creator-requests/{requestId}
 * Body: CreatorRequestPatchRequest
 */
export const patchCreatorRequest = (requestId, payload) => {
  requireRequestId(requestId);

  const body = normalizeCreatorRequestPatchPayload(payload);

  return apiRequest(`/creator-requests/${encId(requestId)}`, "PATCH", body);
};

/**
 * POST /creator-requests/{requestId}/decision
 * Body: CreatorRequestDecisionRunRequest
 *
 * This is the metered action because it runs a Creator Check.
 */
export const runCreatorRequestDecision = (requestId, payload) => {
  requireRequestId(requestId);

  const body = normalizeDecisionRunPayload(payload);

  return apiRequest(
    `/creator-requests/${encId(requestId)}/decision`,
    "POST",
    body
  );
};

/**
 * POST /creator-requests/{requestId}/business-decision
 * Body: CreatorRequestBusinessDecisionRequest
 */
export const recordCreatorRequestBusinessDecision = (requestId, payload) => {
  requireRequestId(requestId);

  const body = normalizeBusinessDecisionPayload(payload);

  return apiRequest(
    `/creator-requests/${encId(requestId)}/business-decision`,
    "POST",
    body
  );
};

/**
 * POST /creator-requests/{requestId}/campaign-link
 * Body: CreatorRequestCampaignLinkRequest
 *
 * Back-office fallback only.
 * This only links an existing campaign/offer/invite.
 * It does not create campaign artifacts.
 */
export const linkCreatorRequestCampaign = (requestId, payload) => {
  requireRequestId(requestId);

  const body = normalizeCampaignLinkPayload(payload);

  return apiRequest(
    `/creator-requests/${encId(requestId)}/campaign-link`,
    "POST",
    body
  );
};

/**
 * POST /creator-requests/{requestId}/convert-to-campaign
 * Body: CreatorRequestConvertToCampaignRequest
 *
 * Preferred production flow after a creator request is accepted.
 * Creates campaign, offer/code, invite/tracking link, and links the IDs back to the request.
 */
export const convertCreatorRequestToCampaign = (requestId, payload) => {
  requireRequestId(requestId);

  const body = normalizeConvertToCampaignPayload(payload);

  return apiRequest(
    `/creator-requests/${encId(requestId)}/convert-to-campaign`,
    "POST",
    body
  );
};